// src/services/api.ts
// localhost fallback is only safe in dev — a missing VITE_API_URL in a
// production build must fail loudly instead of silently hitting localhost.
const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:3002' : '')

export function getApiUrl(): string {
  if (!API_URL) {
    throw new Error(
      'VITE_API_URL is not set. Configure it in .env for local dev, or as a build-time secret in CI/CD for production.'
    )
  }
  return API_URL
}

async function authHeaders(): Promise<HeadersInit> {
  const { supabase } = await import('./supabase')
  const { data: { session } } = await supabase.auth.getSession()
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session?.access_token ?? ''}`,
  }
}

async function getAIBody() {
  const { useSettings } = await import('../stores/useSettings')
  const s = useSettings.getState().settings
  return {
    provider: s.ai_provider,
    api_key: s.user_api_key || undefined,
    ai_context: s.ai_context || undefined,
  }
}

export const api = {
  async classify(text: string): Promise<{ type: 'signal' | 'noise'; reason: string; q1?: boolean; q2?: boolean; q3?: boolean }> {
    const res = await fetch(`${getApiUrl()}/api/classify`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ text, ...(await getAIBody()) }),
    })
    if (!res.ok) throw new Error('Classify failed')
    return res.json()
  },

  async smartImport(text: string): Promise<Array<{ text: string }>> {
    const res = await fetch(`${getApiUrl()}/api/smart-import`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ text, ...(await getAIBody()) }),
    })
    if (!res.ok) throw new Error('Smart import failed')
    return res.json()
  },

  async getCoachNote(payload: {
    snrPct: number
    signalTasks: string[]
    noiseTasks: string[]
    noiseSeconds: number
    signalsDone: number
    pastReflections: string[]
    coaching_tone?: 'mentor' | 'stoic' | 'drill' | 'hype'
  }): Promise<{ coaching?: string; note?: string; text?: string }> {
    const res = await fetch(`${getApiUrl()}/api/coach`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ ...(await getAIBody()), ...payload }),
    })
    if (!res.ok) throw new Error('Coach request failed')
    return res.json()
  },

  async getWeekReview(days: Array<{ date: string; snr_pct: number; reflection_answer: string | null }>) {
    const res = await fetch(`${getApiUrl()}/api/week-review`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ days, ...(await getAIBody()) }),
    })
    if (!res.ok) throw new Error('Week review failed')
    return res.json()
  },
}
